import { Fragment, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Download } from 'lucide-react'
import { toast } from 'sonner'
import { apiError, useAdminPartnersQuery, useCreateGroupMutation, useCreatePartnerMutation, useGroupsQuery } from '@/store/api'
import type { Group, PartnerListRow } from '@shared/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input, Label, FieldError } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Table, THead, Th, Tr, Td, TotalsRow, SkeletonRows } from '@/components/ui/data-table'
import { EmptyState, ErrorState } from '@/components/ui/empty-state'
import { Term } from '@/components/ui/term'
import { PeriodSelect, recentPeriods } from '@/components/ui/period-select'
import { Tooltip } from '@/components/ui/tooltip'
import { downloadCsv } from '@/lib/csv'
import { fmtDate, fmtDateTime, fmtMoney, fmtNum, fmtRelative } from '@/lib/format'

const COLS = 9
const NO_GROUP = '__none'
const NEW_GROUP = '__new'

/** Sum brand-currency amounts per currency — never across currencies. */
function perCurrency(rows: PartnerListRow[]): [string, number][] {
  const totals = new Map<string, number>()
  for (const r of rows) totals.set(r.currency, (totals.get(r.currency) ?? 0) + r.expansionChargesMinor)
  return [...totals.entries()].sort(([a], [b]) => a.localeCompare(b))
}

function GroupHeading({ name, group }: { name: string | null; group?: Group }) {
  if (!group) return <>{name}</>
  return (
    <span className="flex items-center gap-3">
      <Link to={`/g/${group.slug}`} className="hover:underline">
        {group.name}
      </Link>
      <span className="font-normal normal-case tracking-normal">
        {group.billingMode === 'group' ? 'Invoiced once, to the group' : 'Invoiced per brand'}
      </span>
    </span>
  )
}

/**
 * All brands for one Billing Period — the admin list and the invoicing overview in one
 * table. Brands in a hosting group sit under the group's name; a group is invoiced per
 * brand or once for all its brands.
 */
export function PartnersList() {
  const [period, setPeriod] = useState(() => recentPeriods()[0])
  const [showArchived, setShowArchived] = useState(false)
  const { data, isLoading, isError, refetch } = useAdminPartnersQuery({ archived: showArchived, period })
  const { data: groups } = useGroupsQuery()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const sections = useMemo(() => {
    const byGroup = new Map<string, { name: string | null; rows: PartnerListRow[] }>()
    for (const r of data ?? []) {
      const key = r.groupId ?? NO_GROUP
      if (!byGroup.has(key)) byGroup.set(key, { name: r.groupName, rows: [] })
      byGroup.get(key)!.rows.push(r)
    }
    return [...byGroup.entries()]
      .sort(([a, x], [b, y]) => (a === NO_GROUP ? 1 : b === NO_GROUP ? -1 : (x.name ?? '').localeCompare(y.name ?? '')))
      .map(([key, v]) => ({ key, ...v }))
  }, [data])

  const totals = useMemo(() => {
    const rows = data ?? []
    return {
      held: rows.reduce((n, r) => n + r.entitlements.held, 0),
      granted: rows.reduce((n, r) => n + r.entitlements.granted, 0),
      undrawn: rows.reduce((n, r) => n + r.entitlements.undrawn, 0),
      smbFree: rows.reduce((n, r) => n + r.smbFree, 0),
      smbPaid: rows.reduce((n, r) => n + r.smbPaid, 0),
      fees: rows.reduce((n, r) => n + r.licenceFeesCents, 0),
      expansion: perCurrency(rows),
    }
  }, [data])

  const exportCsv = () => {
    if (!data) return
    downloadCsv(
      `atarim-partners-${period}.csv`,
      [
        'Brand',
        'Group',
        'Licence Entitlements',
        'Tier rate (USD)',
        'Licences granted',
        'Undrawn Entitlements',
        'SMB owners free',
        'SMB owners paid',
        'SMB free credits used',
        'Licence Fees (USD)',
        'Expansion Charges',
        'Currency',
        'Flags',
      ],
      data.map((r) => [
        `${r.name} (${r.slug})`,
        r.groupName ?? '',
        r.entitlements.held,
        (r.entitlements.rateCents / 100).toFixed(2),
        r.entitlements.granted,
        r.entitlements.undrawn,
        r.smbFree,
        r.smbPaid,
        r.smbFreeCreditsUsed,
        (r.licenceFeesCents / 100).toFixed(2),
        (r.expansionChargesMinor / 100).toFixed(2),
        r.currency,
        flagText(r),
      ]),
    )
  }

  return (
    <>
      <PageHeader
        title="Partners"
        description={
          <>
            One row per brand for the selected <Term id="billing-period" /> (UTC). <Term id="licence-fee">Licence Fees</Term> are
            in USD; <Term id="expansion-charge">Expansion Charges</Term> are in each brand's currency and stay provisional until
            the period is closed.
          </>
        }
        actions={
          <>
            <PeriodSelect value={period} onChange={setPeriod} />
            <Button variant="secondary" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? 'Hide archived' : 'Show archived'}
            </Button>
            <Button variant="secondary" onClick={exportCsv} disabled={!data?.length}>
              <Download className="size-4" /> Export CSV
            </Button>
            <Button onClick={() => setOpen(true)}>Add Partner</Button>
          </>
        }
      />

      {isError ? (
        <ErrorState onRetry={refetch} />
      ) : !isLoading && data?.length === 0 ? (
        <EmptyState title="No partners yet" action={<Button onClick={() => setOpen(true)}>Add Partner</Button>} />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <Th>Brand</Th>
              <Th right>
                <Term id="licence-entitlement">Entitlements</Term>
              </Th>
              <Th right>
                <Term id="licence-granted">Granted</Term>
              </Th>
              <Th right>
                <Term id="undrawn-entitlement">Undrawn</Term>
              </Th>
              <Th right>
                <Term id="smb-owner">SMB owners</Term> free / paid
              </Th>
              <Th right>
                <Term id="licence-fee">Licence Fees</Term>
              </Th>
              <Th right>
                <Term id="expansion-charge">Expansion Charges</Term>
              </Th>
              <Th>Flags</Th>
              <Th>Last activity</Th>
            </THead>
            {isLoading ? (
              <SkeletonRows cols={COLS} />
            ) : (
              <tbody>
                {sections.map((s) => (
                  <Fragment key={s.key}>
                    {(sections.length > 1 || s.key !== NO_GROUP) && (
                      <tr className="bg-muted/40">
                        <td colSpan={COLS} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {s.key === NO_GROUP ? (
                            'Not in a group'
                          ) : (
                            <GroupHeading name={s.name} group={groups?.find((g) => g.id === s.key)} />
                          )}
                        </td>
                      </tr>
                    )}
                    {s.rows.map((r) => (
                      <PartnerRow key={r.id} row={r} onOpen={() => navigate(`/admin/partners/${r.slug}`)} />
                    ))}
                  </Fragment>
                ))}
                <TotalsRow>
                  <Td>Total</Td>
                  <Td right>{fmtNum(totals.held)}</Td>
                  <Td right>{fmtNum(totals.granted)}</Td>
                  <Td right>{fmtNum(totals.undrawn)}</Td>
                  <Td right>
                    {fmtNum(totals.smbFree)} / {fmtNum(totals.smbPaid)}
                  </Td>
                  <Td right>{fmtMoney(totals.fees, 'USD')}</Td>
                  <Td right>
                    {totals.expansion.length === 0
                      ? '—'
                      : totals.expansion.map(([cur, minor]) => (
                          <div key={cur}>{fmtMoney(minor, cur)}</div>
                        ))}
                  </Td>
                  <Td colSpan={2} />
                </TotalsRow>
              </tbody>
            )}
          </Table>
        </div>
      )}

      <AddPartnerDialog open={open} onOpenChange={setOpen} />
    </>
  )
}

function flagText(r: PartnerListRow): string {
  const flags: string[] = []
  if (r.flags.suspended) flags.push('Suspended')
  if (r.flags.autoAdded) flags.push(`${r.flags.autoAdded} auto-add to confirm`)
  for (const d of r.flags.contractDatesDue) flags.push(`${d.label} ${d.date}`)
  return flags.join('; ')
}

function PartnerRow({ row: r, onOpen }: { row: PartnerListRow; onOpen: () => void }) {
  const e = r.entitlements
  return (
    <Tr clickable onClick={onOpen}>
      <Td className="font-medium">
        <span className="inline-flex flex-wrap items-center gap-2">
          {r.name}
          {r.archivedAt && <Badge variant="neutral">Archived</Badge>}
        </span>
        <span className="block text-xs font-normal text-muted-foreground">
          {r.slug} · {r.currency}
        </span>
      </Td>
      <Td right>
        {fmtNum(e.held)}
        <span className="block text-xs text-muted-foreground">{fmtMoney(e.rateCents, 'USD')} each</span>
      </Td>
      <Td right>{fmtNum(e.granted)}</Td>
      <Td right>{fmtNum(e.undrawn)}</Td>
      <Td right>
        {r.offers.smb ? (
          <>
            {fmtNum(r.smbFree)} / {fmtNum(r.smbPaid)}
            {r.smbFreeCreditsUsed > 0 && (
              <span className="block text-xs text-muted-foreground">{fmtNum(r.smbFreeCreditsUsed)} free credits used</span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">Off</span>
        )}
      </Td>
      <Td right>{fmtMoney(r.licenceFeesCents, 'USD')}</Td>
      <Td right>{fmtMoney(r.expansionChargesMinor, r.currency)}</Td>
      <Td>
        <div className="flex flex-wrap gap-1">
          {r.flags.suspended && <Badge variant="revoked">Suspended</Badge>}
          {r.flags.autoAdded > 0 && (
            <Tooltip content="Licence Entitlements were auto-added after the brand granted more licences than it held. Confirm the new volume with the partner.">
              <span>
                <Badge variant="expiring">Auto-added ×{r.flags.autoAdded}</Badge>
              </span>
            </Tooltip>
          )}
          {r.flags.contractDatesDue.map((d) => (
            <Badge key={d.label} variant="pending">
              {d.label} {fmtDate(d.date)}
            </Badge>
          ))}
        </div>
      </Td>
      <Td>
        {r.lastActivityAt ? (
          <Tooltip content={fmtDateTime(r.lastActivityAt)}>
            <span>{fmtRelative(r.lastActivityAt)}</span>
          </Tooltip>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </Td>
    </Tr>
  )
}

function AddPartnerDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data: groups } = useGroupsQuery()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [groupId, setGroupId] = useState(NO_GROUP)
  const [newGroupName, setNewGroupName] = useState('')
  const [agency, setAgency] = useState(true)
  const [smb, setSmb] = useState(false)
  const [entitlements, setEntitlements] = useState('500')
  const [currency, setCurrency] = useState('USD')
  const [language, setLanguage] = useState('en')
  const [error, setError] = useState<string | null>(null)
  const [create, { isLoading }] = useCreatePartnerMutation()
  const [createGroup, { isLoading: creatingGroup }] = useCreateGroupMutation()
  const navigate = useNavigate()

  const autoSlug = (n: string) =>
    n.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const count = parseInt(entitlements, 10)
    if (!Number.isFinite(count) || count < 500) return setError('Minimum is 500 Licence Entitlements')
    if (count % 100 !== 0) return setError('Licence Entitlements change in steps of 100')
    if (!agency && !smb) return setError('Enable at least one offer')
    if (groupId === NEW_GROUP && !newGroupName.trim()) return setError('Name the new group')
    try {
      let gid: string | null = groupId === NO_GROUP ? null : groupId
      if (groupId === NEW_GROUP) gid = (await createGroup({ name: newGroupName.trim() }).unwrap()).id
      const partner = await create({
        name,
        slug: slug || undefined,
        groupId: gid,
        offers: { agency, smb },
        entitlements: count,
        currency: currency.trim().toUpperCase(),
        language: language.trim(),
      }).unwrap()
      toast.success(`Partner ${partner.name} created`)
      onOpenChange(false)
      navigate(`/admin/partners/${partner.slug}`)
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Add Partner" description="Each brand is its own partner, with its own contract, Entitlements, prices and statement.">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="p-name">Name</Label>
              <Input
                id="p-name"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (!slugTouched) setSlug(autoSlug(e.target.value))
                }}
              />
            </div>
            <div>
              <Label htmlFor="p-slug">Slug</Label>
              <Input
                id="p-slug"
                required
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true)
                  setSlug(e.target.value)
                }}
                onBlur={(e) => setSlug(autoSlug(e.target.value))}
              />
            </div>
          </div>
          <div>
            <Label>Hosting group</Label>
            <Select
              ariaLabel="Hosting group"
              value={groupId}
              onValueChange={setGroupId}
              options={[
                { value: NO_GROUP, label: 'No group' },
                ...(groups ?? []).map((g) => ({ value: g.id, label: g.name })),
                { value: NEW_GROUP, label: 'Create a new group…' },
              ]}
              className="w-full"
            />
            {groupId === NEW_GROUP && (
              <Input className="mt-2" placeholder="Group name" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} />
            )}
          </div>
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium">Offers</legend>
            <div className="flex gap-4 text-sm">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={agency} onChange={(e) => setAgency(e.target.checked)} /> Agency bundle
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={smb} onChange={(e) => setSmb(e.target.checked)} /> SMB
              </label>
            </div>
          </fieldset>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="p-ent">Entitlements</Label>
              <Input id="p-ent" type="number" min={500} step={100} required value={entitlements} onChange={(e) => setEntitlements(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="p-cur">Currency</Label>
              <Input id="p-cur" required maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
            </div>
            <div>
              <Label htmlFor="p-lang">Language</Label>
              <Input id="p-lang" required value={language} onChange={(e) => setLanguage(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Minimum 500, then steps of 100. $10.00 each below 1,000; $7.50 each from 1,000. Everything launches in English; the
            language is stored for later.
          </p>
          <FieldError>{error}</FieldError>
          <Button type="submit" className="w-full" loading={isLoading || creatingGroup}>
            Create partner
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
